import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useSpaces } from "@/context/SpaceContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Folder, Plus, Pencil, Trash2, ArrowLeft, Globe, Lock } from "lucide-react";
import { toast } from "sonner";

const emptyForm = { name: "", everyone: false, ids: {} };

export default function CategoryManager({ open, onClose }) {
  const { spaces, reload, setActiveSpaceId } = useSpaces();
  const [users, setUsers] = useState([]);
  const [mode, setMode] = useState("list"); // list | edit
  const [editing, setEditing] = useState(null); // space obj or null (new)
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMode("list");
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, [open]);

  const openForm = (space) => {
    setEditing(space);
    if (space) {
      const ids = {};
      (space.view_user_ids || []).forEach((id) => { ids[id] = "view"; });
      (space.edit_user_ids || []).forEach((id) => { ids[id] = "edit"; });
      setForm({ name: space.name, everyone: !!space.everyone, ids });
    } else {
      setForm(emptyForm);
    }
    setMode("edit");
  };

  const toggleUser = (uid) => {
    setForm((f) => {
      const cur = f.ids[uid];
      const next = { ...f.ids };
      if (!cur) next[uid] = "view";
      else if (cur === "view") next[uid] = "edit";
      else delete next[uid];
      return { ...f, ids: next };
    });
  };

  const save = async () => {
    const name = form.name.trim();
    if (!name) return toast.error("Name is required");
    setBusy(true);
    const view_user_ids = [];
    const edit_user_ids = [];
    Object.entries(form.ids).forEach(([uid, lv]) => {
      if (lv === "view") view_user_ids.push(uid);
      if (lv === "edit") { edit_user_ids.push(uid); view_user_ids.push(uid); }
    });
    const payload = { name, everyone: form.everyone, view_user_ids, edit_user_ids };
    try {
      if (editing?.id) {
        await api.patch(`/spaces/${editing.id}`, payload);
        toast.success("Category updated");
      } else {
        const { data } = await api.post("/spaces", payload);
        toast.success("Category created");
        if (data?.id) setActiveSpaceId(data.id);
      }
      await reload();
      setMode("list");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save category");
    } finally { setBusy(false); }
  };

  const remove = async (space) => {
    if (!window.confirm(`Delete category "${space.name}"? This can't be undone.`)) return;
    try {
      await api.delete(`/spaces/${space.id}`);
      toast.success("Category deleted");
      await reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete category");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto vault-scroll" data-testid="category-manager">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            {mode === "edit" && (
              <button onClick={() => setMode("list")} data-testid="category-back-btn" className="text-gray-400 hover:text-gray-700">
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            {mode === "list" ? "Categories" : editing ? "Edit category" : "New category"}
          </DialogTitle>
        </DialogHeader>

        {mode === "list" ? (
          <div className="space-y-2 py-1">
            {spaces.map((s) => (
              <div key={s.id} data-testid={`category-row-${s.id}`}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-gray-200 bg-white">
                <Folder className="w-4 h-4 text-[#1B4332] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate flex items-center gap-1.5">
                    {s.name}
                    {s.is_general && <Badge variant="outline" className="text-[10px] px-1.5 py-0">Default</Badge>}
                  </div>
                  <div className="text-[11px] text-gray-400 flex items-center gap-1">
                    {s.everyone ? <><Globe className="w-3 h-3" /> Everyone</> : <><Lock className="w-3 h-3" /> Restricted</>}
                    <span>· {s.count} item{s.count === 1 ? "" : "s"}</span>
                  </div>
                </div>
                {s.can_manage && (
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => openForm(s)} data-testid={`category-edit-${s.id}`}
                      className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    {!s.is_general && (
                      <button onClick={() => remove(s)} data-testid={`category-delete-${s.id}`}
                        className="p-1.5 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
            <Button onClick={() => openForm(null)} data-testid="category-new-btn"
              className="w-full mt-1 bg-[#1B4332] hover:bg-[#14472F]">
              <Plus className="w-4 h-4 mr-1.5" /> New category
            </Button>
          </div>
        ) : (
          <div className="space-y-4 py-1">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input value={form.name} data-testid="category-name-input"
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Legal, HR, Finance" autoFocus />
            </div>

            <div className="flex items-center justify-between rounded-md border border-gray-200 px-3 py-2.5">
              <div>
                <div className="text-sm font-medium">Visible to everyone</div>
                <div className="text-[11px] text-gray-400">All colleagues can see this category</div>
              </div>
              <Switch checked={form.everyone} onCheckedChange={(v) => setForm((f) => ({ ...f, everyone: v }))} data-testid="category-everyone-switch" />
            </div>

            {!form.everyone && (
              <div className="space-y-1.5">
                <Label>People with access</Label>
                <p className="text-[11px] text-gray-400">Tap a person to cycle: none → view → view + modify. You and admins always have access.</p>
                <div className="border border-gray-200 rounded-md divide-y divide-gray-100 max-h-56 overflow-y-auto vault-scroll">
                  {users.map((u) => {
                    const lv = form.ids[u.id];
                    return (
                      <button key={u.id} onClick={() => toggleUser(u.id)} data-testid={`category-user-${u.id}`}
                        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-gray-50">
                        <div className="min-w-0">
                          <div className="text-sm truncate">{u.name}</div>
                          <div className="text-[11px] text-gray-400 truncate">{u.email}</div>
                        </div>
                        <Badge variant="outline" className={
                          lv === "edit" ? "text-green-700 bg-green-50 border-green-200"
                          : lv === "view" ? "text-blue-700 bg-blue-50 border-blue-200"
                          : "text-gray-400"
                        }>{lv === "edit" ? "Modify" : lv === "view" ? "View" : "No access"}</Badge>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setMode("list")} data-testid="category-cancel-btn">Cancel</Button>
              <Button onClick={save} disabled={busy} data-testid="category-save-btn" className="bg-[#0A0A0A] hover:bg-gray-800">
                {busy ? "Saving…" : "Save category"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
